import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Sparkles } from "lucide-react";

const inputCls = "bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500";
const VAR_RE = /\{([a-z_]+)(?:\|([^{}]*))?\}/g;

// Mesma regra do servidor: {nome} ou {nome|padrao}; aqui so pra PREVIA com dados de exemplo.
function preview(text, examples) {
  return (text || "").replace(VAR_RE, (m, name, def) => {
    if (examples[name]) return examples[name];
    return def !== undefined ? def : m;
  });
}

function unknownVars(text, examples) {
  const out = new Set();
  (text || "").replace(VAR_RE, (m, name) => { if (!(name in examples)) out.add(name); return m; });
  return [...out];
}

export default function QuickRepliesEditor({ value, onChange }) {
  const [vars, setVars] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const areas = useRef({});
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    api.get("/quick-replies/variables")
      .then(({ data }) => { setVars(data.variables || []); setSuggestions(data.suggestions || []); })
      .catch(() => {});
  }, []);

  const examples = Object.fromEntries(vars.map((v) => [v.name, v.example]));
  const list = value || [];

  const setItem = (i, patch) => onChange(list.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const insertVar = (name) => {
    const i = focused ?? (list.length ? list.length - 1 : null);
    if (i === null) return;
    const el = areas.current[i];
    const q = list[i];
    const token = `{${name}}`;
    const start = el?.selectionStart ?? (q.text || "").length;
    const end = el?.selectionEnd ?? start;
    const next = (q.text || "").slice(0, start) + token + (q.text || "").slice(end);
    setItem(i, { text: next });
    requestAnimationFrame(() => {
      const e2 = areas.current[i];
      if (e2) { e2.focus(); e2.setSelectionRange(start + token.length, start + token.length); }
    });
  };

  const missingSuggestions = suggestions.filter((s) => !list.some((q) => q.label === s.label));

  return (
    <div className="border-t border-slate-800 pt-4">
      <div className="text-slate-300 text-sm mb-1">Respostas rápidas (atendimento)</div>
      <p className="text-xs text-slate-500 leading-relaxed mb-3">
        Aparecem como botões na conversa — e também ao digitar <b>/</b> no campo de resposta. Use{" "}
        <b>variáveis</b> para preencher sozinho com os dados do cliente da conversa. Se o dado não existir,
        o botão fica desativado; para ter um texto reserva, use <code className="text-emerald-300">{"{primeiro_nome|cliente}"}</code>.
      </p>

      {vars.length > 0 && (
        <div className="mb-3" data-testid="quick-reply-vars">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">
            Variáveis — clique para inserir no texto selecionado
          </div>
          <div className="flex flex-wrap gap-1.5">
            {vars.map((v) => (
              <button
                key={v.name} type="button" onClick={() => insertVar(v.name)}
                title={`${v.description} — ex.: ${v.example}`}
                className="text-[11px] font-mono-jb bg-slate-800 hover:bg-slate-700 border border-slate-700 text-emerald-300 rounded px-2 py-0.5"
              >
                {`{${v.name}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {list.map((qr, i) => {
          const bad = unknownVars(qr.text, examples);
          return (
            <div key={i} className="space-y-1">
              <div className="flex gap-2 items-start">
                <Input
                  data-testid={`quick-reply-label-${i}`}
                  placeholder="Rótulo (ex.: Bom dia)"
                  maxLength={40}
                  value={qr.label}
                  onChange={(e) => setItem(i, { label: e.target.value })}
                  className={`${inputCls} w-48 shrink-0`}
                />
                <Textarea
                  data-testid={`quick-reply-text-${i}`}
                  ref={(el) => { areas.current[i] = el; }}
                  onFocus={() => setFocused(i)}
                  placeholder="Texto completo — ex.: {saudacao}, {primeiro_nome|cliente}!"
                  rows={2}
                  value={qr.text}
                  onChange={(e) => setItem(i, { text: e.target.value })}
                  className={`${inputCls} flex-1 min-h-0`}
                />
                <Button
                  type="button" size="sm" variant="outline"
                  data-testid={`quick-reply-remove-${i}`}
                  onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                  className="border-slate-700 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300 shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              {bad.length > 0 ? (
                <div className="text-[11px] text-red-400 pl-[200px]">
                  Variável desconhecida: {bad.map((b) => `{${b}}`).join(", ")} — use a lista acima.
                </div>
              ) : /\{[a-z_]+/.test(qr.text || "") ? (
                <div className="text-[11px] text-slate-500 pl-[200px]">
                  <span className="text-slate-600">Prévia (cliente de exemplo): </span>{preview(qr.text, examples)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button
          type="button" size="sm" variant="outline"
          data-testid="quick-reply-add-btn"
          onClick={() => onChange([...list, { label: "", text: "" }])}
          className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Adicionar resposta rápida
        </Button>
        {missingSuggestions.length > 0 && (
          <Button
            type="button" size="sm" variant="outline"
            data-testid="quick-reply-suggestions-btn"
            onClick={() => onChange([...list, ...missingSuggestions])}
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Adicionar {missingSuggestions.length} sugestões inteligentes
          </Button>
        )}
      </div>
    </div>
  );
}
